// src/pages/AddHireAgreement.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Divider,
  Alert,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { useNavigate } from "react-router-dom";
import useDrivers from "../hooks/useDrivers";
import useVehicles from "../hooks/useVehicles";
import { createHireAgreement } from "../api/hireAgreements";

export default function AddHireAgreement() {
  const navigate = useNavigate();
  const { drivers } = useDrivers();
  const { vehicles } = useVehicles();

  const [form, setForm] = useState({
    driver_id: "",
    vehicle_id: "",

    handover_date: "",
    start_date: "",
    end_date: "",

    currency: "GBP",
    deposit_amount_pence: 0,
    weekly_amount_pence: 0,

    insurance_included: false,
    insurance_price_pence: 0,

    renter_licence_number: "",
    status: "draft",
    end_reason: "",
  });

  const [saving, setSaving] = useState(false);

  // validation + generic error state
  const [errors, setErrors] = useState({});
  const [genericError, setGenericError] = useState("");

  const updateField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const clearFieldError = (field) =>
    setErrors((prev) => ({ ...prev, [field]: undefined }));

  const toUnix = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return Math.floor(d.getTime() / 1000);
  };

  const handleSubmit = async () => {
    setSaving(true);
    setErrors({});
    setGenericError("");

    try {
      const payload = {
        driver_id: form.driver_id,
        vehicle_id: form.vehicle_id,
        handover_date: toUnix(form.handover_date),
        start_date: toUnix(form.start_date),
        end_date: toUnix(form.end_date),

        currency: form.currency,
        deposit_amount_pence: Number(form.deposit_amount_pence),
        weekly_amount_pence: Number(form.weekly_amount_pence),

        insurance_included: form.insurance_included,
        insurance_price_pence: form.insurance_included
          ? 0
          : Number(form.insurance_price_pence || 0),

        renter_licence_number: form.renter_licence_number,
        status: form.status,
        end_reason:
          form.status === "ended" || form.status === "cancelled"
            ? form.end_reason
            : "",
      };

      const data = await createHireAgreement(payload);
      // backend may return { id } or { _id } – support both
      const newId = data.id || data._id;
      if (!newId) {
        // fallback: just go back to list
        navigate("/hire-agreements");
      } else {
        navigate(`/hire-agreements/${newId}`);
      }
    } catch (err) {
      console.error("Failed to create hire agreement:", err);
      if (err.status === 400 && err.validationErrors) {
        // express-validator: { field: "message", ... }
        setErrors(err.validationErrors || {});
      } else {
        setGenericError(
          "A server error occurred while creating the hire agreement. Please try again."
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1 }}>
        New Hire Agreement
      </Typography>

      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        Assign a vehicle and driver, then generate a hire agreement.
      </Typography>

      {genericError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setGenericError("")}
        >
          {genericError}
        </Alert>
      )}

      <Paper sx={{ p: 3, borderRadius: 2 }}>
        {/* ================================
            DRIVER + VEHICLE
        ================================== */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Hire Agreement Parties
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{xs: 12, sm: 6}}>
            <Autocomplete
              options={drivers}
              getOptionLabel={(d) =>
                `${d.firstname || ""} ${d.lastname || ""}`.trim()
              }
              onChange={(e, val) => {
                updateField("driver_id", val ? val._id : "");
                clearFieldError("driver_id");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Driver"
                  fullWidth
                  error={!!errors.driver_id}
                  helperText={errors.driver_id}
                />
              )}
            />
          </Grid>

          <Grid item size={{xs: 12, sm: 6}}>
            <Autocomplete
              options={vehicles}
              getOptionLabel={(v) =>
                `${v.vrm} – ${v.make_model || v.make || ""}`
              }
              onChange={(e, val) => {
                updateField("vehicle_id", val ? val._id : "");
                clearFieldError("vehicle_id");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Vehicle"
                  fullWidth
                  error={!!errors.vehicle_id}
                  helperText={errors.vehicle_id}
                />
              )}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* ================================
            HIRE PERIOD
        ================================== */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Hire Period
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{xs: 12, sm: 4}}>
            <TextField
              label="Handover Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.handover_date}
              onChange={(e) => {
                updateField("handover_date", e.target.value);
                clearFieldError("handover_date");
              }}
              error={!!errors.handover_date}
              helperText={errors.handover_date}
            />
          </Grid>

          <Grid item size={{xs: 12, sm: 4}}>
            <TextField
              label="Start Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.start_date}
              onChange={(e) => {
                updateField("start_date", e.target.value);
                clearFieldError("start_date");
              }}
              error={!!errors.start_date}
              helperText={errors.start_date}
            />
          </Grid>

          <Grid item size={{xs: 12, sm: 4}}>
            <TextField
              label="End Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.end_date}
              onChange={(e) => {
                updateField("end_date", e.target.value);
                clearFieldError("end_date");
              }}
              error={!!errors.end_date}
              helperText={errors.end_date}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* ================================
            FINANCIAL DETAILS
        ================================== */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Financial Details
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{xs: 12, sm: 4}}>
            <TextField
              label="Currency"
              select
              fullWidth
              value={form.currency}
              onChange={(e) => {
                updateField("currency", e.target.value);
                clearFieldError("currency");
              }}
              error={!!errors.currency}
              helperText={errors.currency}
            >
              <MenuItem value="GBP">GBP</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="EUR">EUR</MenuItem>
            </TextField>
          </Grid>

          <Grid item size={{xs: 12, sm: 4}}>
            <TextField
              label="Deposit (pence)"
              type="number"
              fullWidth
              value={form.deposit_amount_pence}
              onChange={(e) => {
                updateField("deposit_amount_pence", e.target.value);
                clearFieldError("deposit_amount_pence");
              }}
              error={!!errors.deposit_amount_pence}
              helperText={errors.deposit_amount_pence}
            />
          </Grid>

          <Grid item size={{xs: 12, sm: 4}}>
            <TextField
              label="Weekly Amount (pence)"
              type="number"
              fullWidth
              value={form.weekly_amount_pence}
              onChange={(e) => {
                updateField("weekly_amount_pence", e.target.value);
                clearFieldError("weekly_amount_pence");
              }}
              error={!!errors.weekly_amount_pence}
              helperText={errors.weekly_amount_pence}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* ================================
            INSURANCE
        ================================== */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Insurance
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{xs: 12}}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.insurance_included}
                  onChange={(e) => {
                    updateField("insurance_included", e.target.checked);
                    clearFieldError("insurance_included");
                  }}
                />
              }
              label="Insurance Included"
            />
          </Grid>

          {form.insurance_included && (
            <Grid item size={{xs: 12, sm: 6}}>
              <TextField
                label="Insurance Price (pence)"
                type="number"
                fullWidth
                value={form.insurance_price_pence}
                onChange={(e) => {
                  updateField("insurance_price_pence", e.target.value);
                  clearFieldError("insurance_price_pence");
                }}
                error={!!errors.insurance_price_pence}
                helperText={errors.insurance_price_pence}
              />
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* ================================
            RENTER DETAILS
        ================================== */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Renter Details
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{xs: 12, sm: 6}}>
            <TextField
              label="Renter Licence Number"
              fullWidth
              value={form.renter_licence_number}
              onChange={(e) => {
                updateField("renter_licence_number", e.target.value);
                clearFieldError("renter_licence_number");
              }}
              error={!!errors.renter_licence_number}
              helperText={errors.renter_licence_number}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* ================================
            AGREEMENT STATUS
        ================================== */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Agreement Status
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{xs: 12, sm: 6}}>
            <TextField
              label="Status"
              select
              fullWidth
              value={form.status}
              onChange={(e) => {
                updateField("status", e.target.value);
                clearFieldError("status");
              }}
              error={!!errors.status}
              helperText={errors.status}
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="ended">Ended</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </TextField>
          </Grid>

          {(form.status === "ended" || form.status === "cancelled") && (
            <Grid item xs={12}>
              <TextField
                label="End Reason"
                fullWidth
                value={form.end_reason}
                onChange={(e) => {
                  updateField("end_reason", e.target.value);
                  clearFieldError("end_reason");
                }}
                error={!!errors.end_reason}
                helperText={errors.end_reason}
              />
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* ================================
            ACTION BUTTONS
        ================================== */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            variant="outlined"
            sx={{ textTransform: "none" }}
            onClick={() => navigate("/hire-agreements")}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            sx={{ textTransform: "none" }}
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? <CircularProgress size={22} /> : "Create & Generate PDF"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
